import React, { useState } from 'react';
import { CheckCircle2, AlertCircle, Clock, RotateCw, Trash2, ShieldOff } from 'lucide-react';
import ReplyComposer from './ReplyComposer';
import AttachmentPreview from './AttachmentPreview';
import { STATUS_OPTIONS } from './leadStatus';
import { formatManilaTime } from '../../../utils/formatManilaTime';

const formatTime = (iso) => formatManilaTime(iso, { withYear: true });

const StatusBadge = ({ status, failureReason, onRetry, retrying, onSendAnyway, sendingAnyway, canSendAnyway }) => {
  if (status === 'sent') return <span className="inline-flex items-center gap-1 text-xs text-emerald-600"><CheckCircle2 className="w-3 h-3" /> Sent</span>;
  if (status === 'suppressed') {
    return (
      <span className="inline-flex items-center gap-2 text-xs text-purple-300" data-testid="lead-message-suppressed-badge">
        <ShieldOff className="w-3 h-3" /> Test Mode — not sent
        {canSendAnyway && (
          <button
            type="button"
            onClick={onSendAnyway}
            disabled={sendingAnyway}
            data-testid="lead-message-send-anyway-button"
            className="inline-flex items-center gap-1 bg-purple-500/20 hover:bg-purple-500/30 text-purple-200 px-2 py-0.5 rounded-full font-medium disabled:opacity-50"
          >
            <RotateCw className={`w-3 h-3 ${sendingAnyway ? 'animate-spin' : ''}`} /> {sendingAnyway ? 'Sending…' : 'Send Anyway'}
          </button>
        )}
      </span>
    );
  }
  if (status === 'failed') {
    const reasonLabel = failureReason ? `Failed — ${failureReason}` : 'Failed to send';
    return (
      <span className="inline-flex items-center gap-2 text-xs text-red-400" data-testid="lead-message-failed-badge">
        <AlertCircle className="w-3 h-3" /> {reasonLabel}
        <button
          type="button"
          onClick={onRetry}
          disabled={retrying}
          data-testid="lead-message-retry-button"
          className="inline-flex items-center gap-1 bg-red-500/20 hover:bg-red-500/30 text-red-200 px-2 py-0.5 rounded-full font-medium disabled:opacity-50"
        >
          <RotateCw className={`w-3 h-3 ${retrying ? 'animate-spin' : ''}`} /> {retrying ? 'Retrying…' : 'Retry'}
        </button>
      </span>
    );
  }
  if (status === 'received') return <span className="inline-flex items-center gap-1 text-xs text-slate-400"><Clock className="w-3 h-3" /> Received</span>;
  return null;
};

const MessageBubble = ({ message, onRetry, canManage }) => {
  const [retrying, setRetrying] = useState(false);
  const [sendingAnyway, setSendingAnyway] = useState(false);
  const isOutbound = message.direction === 'outbound';
  const senderLabel = isOutbound
    ? `${message.sender_id || 'Admin'} <${message.from_email}>`
    : `${message.from_email || 'Lead'}`;

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await onRetry(message.id, false);
    } finally {
      setRetrying(false);
    }
  };

  const handleSendAnyway = async () => {
    setSendingAnyway(true);
    try {
      await onRetry(message.id, true);
    } finally {
      setSendingAnyway(false);
    }
  };

  return (
    <div className={`flex ${isOutbound ? 'justify-end' : 'justify-start'}`} data-testid={`lead-message-${message.id}`}>
      <div className={`max-w-[75%] rounded-2xl px-4 py-3 ${isOutbound ? 'bg-slate-900 text-white' : 'bg-slate-100 text-slate-900'}`}>
        <div className={`flex items-center justify-between gap-3 text-xs mb-1 ${isOutbound ? 'text-slate-300' : 'text-slate-500'}`}>
          <span className="font-medium truncate">{senderLabel}</span>
          <span className="whitespace-nowrap">{formatTime(message.created_at)}</span>
        </div>
        <p className="text-sm whitespace-pre-wrap break-words">{message.body}</p>
        {(message.attachments || []).length > 0 && (
          <div className="flex flex-wrap items-start gap-2 mt-2">
            {message.attachments.map((att) => (
              <AttachmentPreview key={att.id} attachment={att} dark={isOutbound} />
            ))}
          </div>
        )}
        <div className="mt-1.5">
          <StatusBadge
            status={message.status}
            failureReason={message.failure_reason}
            onRetry={handleRetry}
            retrying={retrying}
            onSendAnyway={handleSendAnyway}
            sendingAnyway={sendingAnyway}
            canSendAnyway={canManage}
          />
        </div>
      </div>
    </div>
  );
};

const LeadThread = ({
  thread, loading, onSendReply, onRetry, sending, onStatusChange, onDelete, deleting,
  role = 'admin', canManage = true, teamMembers = [], onAssign,
  notes = [], activity = [], onAddNote,
}) => {
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [detailsTab, setDetailsTab] = useState('notes');
  const [noteDraft, setNoteDraft] = useState('');

  if (loading) {
    return (
      <div className="flex-1 bg-white rounded-xl shadow-sm border border-slate-200 flex items-center justify-center min-h-[50vh]">
        <p className="text-sm text-slate-400 italic">Loading conversation…</p>
      </div>
    );
  }

  if (!thread) {
    return (
      <div className="flex-1 bg-white rounded-xl shadow-sm border border-slate-200 flex items-center justify-center min-h-[50vh]" data-testid="lead-thread-empty-state">
        <p className="text-sm text-slate-400">Select a lead to view the conversation.</p>
      </div>
    );
  }

  const { lead, conversation, messages } = thread;

  const handleDeleteClick = () => {
    if (!confirmingDelete) {
      setConfirmingDelete(true);
      return;
    }
    setConfirmingDelete(false);
    onDelete();
  };

  return (
    <div className="flex-1 bg-white rounded-xl shadow-sm border border-slate-200 flex flex-col min-h-[50vh]" data-testid="lead-thread">
      <div className="p-4 border-b border-slate-200 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-semibold text-slate-900" data-testid="lead-thread-name">{lead.name}</h3>
          <p className="text-sm text-slate-500">
            {lead.email} · <span className="capitalize">{lead.preferred_language}</span>
            {conversation?.assigned_via_voucher && lead.voucher_code && (
              <span
                className="ml-2 inline-flex items-center gap-1 text-[11px] font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full px-2 py-0.5"
                data-testid="lead-thread-voucher-code"
              >
                Voucher: {lead.voucher_code}
              </span>
            )}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap">
          {canManage && (
            <select
              value={conversation?.assigned_manager_id || ''}
              onChange={(e) => onAssign(e.target.value)}
              data-testid="lead-assign-select"
              className="text-sm border-2 border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-slate-900"
            >
              <option value="">Unassigned</option>
              {teamMembers.map((m) => (
                <option key={m.username} value={m.username}>{m.name}</option>
              ))}
            </select>
          )}
          <select
            value={conversation?.status || 'new'}
            onChange={(e) => onStatusChange(e.target.value)}
            data-testid="lead-status-select"
            className="text-sm border-2 border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-slate-900"
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
          {canManage && (
            <button
              type="button"
              onClick={handleDeleteClick}
              onBlur={() => setConfirmingDelete(false)}
              disabled={deleting}
              data-testid="lead-delete-button"
              className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg font-medium transition-colors disabled:opacity-50 ${
                confirmingDelete ? 'bg-red-600 text-white' : 'bg-red-50 text-red-600 hover:bg-red-100'
              }`}
            >
              <Trash2 className="w-4 h-4" /> {confirmingDelete ? 'Confirm delete?' : 'Delete'}
            </button>
          )}
          <button
            type="button"
            onClick={() => setShowDetails((s) => !s)}
            data-testid="lead-details-toggle"
            className="text-sm px-3 py-1.5 rounded-lg border border-slate-200 text-slate-600 hover:border-slate-400 transition-colors"
          >
            Details{(notes.length + activity.length) > 0 ? ` (${notes.length + activity.length})` : ''}
          </button>
        </div>
      </div>
      {showDetails && (
        <div className="border-b border-slate-200 bg-slate-50 p-4" data-testid="lead-details-panel">
          <div className="flex gap-2 mb-3">
            <button
              type="button"
              onClick={() => setDetailsTab('notes')}
              data-testid="lead-details-tab-notes"
              className={`text-xs font-medium px-3 py-1 rounded-full transition-colors ${detailsTab === 'notes' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}
            >
              Notes
            </button>
            <button
              type="button"
              onClick={() => setDetailsTab('activity')}
              data-testid="lead-details-tab-activity"
              className={`text-xs font-medium px-3 py-1 rounded-full transition-colors ${detailsTab === 'activity' ? 'bg-slate-900 text-white' : 'bg-white text-slate-600 border border-slate-200'}`}
            >
              Activity
            </button>
          </div>
          {detailsTab === 'notes' ? (
            <div className="space-y-2 max-h-48 overflow-y-auto" data-testid="lead-notes-list">
              {notes.length === 0 && <p className="text-xs text-slate-400 italic">No notes yet.</p>}
              {notes.map((n) => (
                <div key={n.id} className="bg-white border border-slate-200 rounded-lg p-2 text-sm" data-testid={`lead-note-${n.id}`}>
                  <p className="text-slate-800">{n.body}</p>
                  <p className="text-[11px] text-slate-400 mt-1">{n.author_username} ({n.author_role}) — {formatTime(n.created_at)}</p>
                </div>
              ))}
              <form
                onSubmit={(e) => { e.preventDefault(); if (!noteDraft.trim()) return; onAddNote?.(noteDraft.trim()); setNoteDraft(''); }}
                className="flex gap-2 pt-1"
              >
                <input
                  value={noteDraft}
                  onChange={(e) => setNoteDraft(e.target.value)}
                  placeholder="Add an internal note…"
                  data-testid="lead-note-input"
                  className="flex-1 text-sm border border-slate-200 rounded-lg px-2 py-1.5 focus:outline-none focus:ring-2 focus:ring-slate-900"
                />
                <button type="submit" data-testid="lead-note-submit" className="text-sm bg-slate-900 text-white px-3 py-1.5 rounded-lg">Add</button>
              </form>
            </div>
          ) : (
            <div className="space-y-1.5 max-h-48 overflow-y-auto" data-testid="lead-activity-list">
              {activity.length === 0 && <p className="text-xs text-slate-400 italic">No activity yet.</p>}
              {activity.map((ev) => (
                <div key={ev.id} className="text-xs text-slate-600 border-b border-slate-100 pb-1.5">
                  <span className="font-medium text-slate-800">{ev.description}</span>
                  <span className="block text-slate-400">{formatTime(ev.created_at)}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 max-h-[50vh]" data-testid="lead-thread-messages">
        {messages.map((m) => <MessageBubble key={m.id} message={m} onRetry={onRetry} canManage={canManage} />)}
      </div>
      <ReplyComposer onSend={onSendReply} sending={sending} canPickSender={canManage} />
    </div>
  );
};

export default LeadThread;

